import dayjs from "dayjs";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import ApplicationTexts from "./ApplicationTexts";
import {
    downloadTemplate,
    uploadTemplate,
    type UploadResult,
    type Usage,
} from "../../../../clients/documentExports/api";
import useApplicationsForExports from "../../../../clients/documentExports/useApplicationsForExports";
import useNotifications from "../../../../components/Notifications";
import * as UI from "../../../../modules/ui-kit";

import type { DocumentExportsOutletContext } from "../../index";

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    h2 {
        font-size: 2rem;
        letter-spacing: -.05em;
        margin: 0;

        small {
            color: #889;
            font-size: 1rem;
            font-weight: normal;
            letter-spacing: 0;
        }
    }

    h3 {
        color: #334;
        font-size: 1em;
        font-weight: 500;
        margin: 0 0 8px;
    }

    .usage {
        max-width: 360px;
    }

    .hint {
        color: #889;
    }

    /* on a checkerboard, so a white or transparent logo shows */
    .preview {
        background: repeating-conic-gradient(#e4e4e8 0 25%, #fff 0 50%) 0 0 / 16px 16px;
        border: 1px solid #dde;
        display: block;
        margin: 0 0 16px;
        max-height: 120px;
        max-width: 320px;
        padding: 8px;
    }

    dl {
        display: grid;
        gap: 4px 16px;
        grid-template-columns: max-content 1fr;
        margin: 0 0 16px;

        dt {
            color: #889;
        }

        dd {
            margin: 0;
        }
    }

    ul {
        margin: 0;
        padding-left: 20px;
    }

    .outcome {
        border-left: 4px solid;
        padding: 8px 16px;

        h3 {
            margin: 0 0 8px;
        }

        p {
            margin: 0 0 8px;
        }

        &.stored {
            border-color: #2a7;
            h3 { color: #2a7; }
        }

        &.rejected {
            border-color: #c32;
            h3 { color: #c32; }
        }
    }

    .errors {
        color: #c32;
    }

    .warnings {
        color: #a60;
    }

    .progress {
        color: #889;
        font-size: .9em;
    }
`;

function formatDate(value?: string) {
    return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—";
}

// Same rule as the service (AdminController.MIN_COMMENT_LENGTH): every change needs a reason.
const MIN_COMMENT_LENGTH = 5;

// ".png, .jpg or .svg" for messages.
function listOf(items: string[]) {
    return items.length > 1 ? `${ items.slice(0, -1).join(", ") } or ${ items[items.length - 1] }` : items[0] ?? "";
}

// One application's file for a chosen usage — a template, or the logo — what is stored
// now, and a form to upload or replace it. The usage dropdown starts empty on every
// application so the choice of where an upload belongs is always deliberate. The service
// validates the file and answers with errors/warnings.
export default function Template() {
    const { applicationDomain = "" } = useParams();
    const { templates, usages, dataProperties, loading, reload } = useOutletContext<DocumentExportsOutletContext>();
    const applications = useApplicationsForExports();
    const notifications = useNotifications();

    const application = applications.find((candidate) => candidate.domain === applicationDomain);
    const [usage, setUsage] = useState<Usage | null>(null);
    const meta = usage ? templates[applicationDomain.toLowerCase()]?.[usage] : undefined;
    const option = usages.find((candidate) => candidate.value === usage);
    const isImage = option?.kind === "image";
    const noun = isImage ? "logo" : "template";
    const accept = option?.accept ?? [];

    const fileInput = useRef<HTMLInputElement>(null);
    const [comment, setComment] = useState("");
    const [overwrite, setOverwrite] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState<number | null>(null);
    const [result, setResult] = useState<UploadResult | null>(null);

    // The current logo, from a blob: the download needs the bearer, so no plain <img src>.
    // Re-fetched when the stored file or its update time changes (after an upload).
    const [preview, setPreview] = useState<string | null>(null);
    const previewFile = isImage ? meta?.file : undefined;
    const previewStamp = meta?.updated;
    useEffect(
        () => {
            if (!previewFile) {
                setPreview(null);
                return undefined;
            }
            let url: string | null = null;
            let cancelled = false;
            downloadTemplate(applicationDomain, previewFile)
                .then((blob) => {
                    if (cancelled) return;
                    url = URL.createObjectURL(blob);
                    setPreview(url);
                })
                .catch(() => setPreview(null));
            return () => {
                cancelled = true;
                if (url) URL.revokeObjectURL(url);
            };
        },
        [applicationDomain, previewFile, previewStamp],
    );

    function selectUsage(next: Usage | null) {
        setUsage(next);
        setResult(null);
        setOverwrite(false);
    }

    // A new application starts with no usage chosen.
    useEffect(
        () => {
            selectUsage(null);
        },
        [applicationDomain],
    );

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        if (!usage) {
            notifications.error("Choose where the upload belongs first");
            return;
        }
        const file = fileInput.current?.files?.[0];
        if (!file) {
            notifications.error(`Choose a ${ listOf(accept) } file first`);
            return;
        }
        // The service refuses a change without a reason; say so before the upload starts.
        if (comment.trim().length < MIN_COMMENT_LENGTH) {
            notifications.error(`Describe the change in a comment (at least ${ MIN_COMMENT_LENGTH } characters)`);
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const outcome = await uploadTemplate(
                { application: applicationDomain, usage, file, comment, overwrite },
                (uploaded, total) => setProgress(Math.round((100 * uploaded) / total)),
            );
            setResult(outcome);
            if (outcome.ok) {
                notifications.success(`Stored ${ outcome.stored }`);
                setComment("");
                setOverwrite(false);
                if (fileInput.current) fileInput.current.value = "";
                await reload();
            } else {
                notifications.error(outcome.errors[0] ?? "Upload rejected");
            }
        } catch (error) {
            notifications.error(`Upload failed: ${ (error as Error).message }`);
        } finally {
            setBusy(false);
            setProgress(null);
        }
    }

    async function handleDownload() {
        if (!meta?.file) return;
        try {
            const blob = await downloadTemplate(applicationDomain, meta.file);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `${ applicationDomain }-${ meta.file }`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            notifications.error(`Download failed: ${ (error as Error).message }`);
        }
    }

    return (
        <Styled>
            <h2>{ application?.name ?? applicationDomain } <small>[{ applicationDomain }.]</small></h2>

            <ApplicationTexts applicationDomain={ applicationDomain } properties={ dataProperties } />

            <UI.Field className="usage">
                <UI.FieldLabel required>Where the upload belongs</UI.FieldLabel>
                <UI.Select
                    isDisabled={ busy }
                    onSelect={ (value) => selectUsage(value as Usage) }
                    options={ usages }
                    placeholder="Choose…"
                    value={ usage ?? "" }
                />
            </UI.Field>

            { !usage && <span className="hint">Choose where the upload belongs to see the current file and upload.</span> }

            { usage && (
                <section>
                    <h3>Current { isImage ? "logo" : `${ usage } template` }</h3>
                    { loading && <span>loading</span> }
                    { !loading && !meta && <span>no { noun } yet — upload one below</span> }
                    { !loading && meta && (
                        <>
                            { preview && <img alt={ `${ applicationDomain } logo` } className="preview" src={ preview } /> }
                            <dl>
                                <dt>created</dt>
                                <dd>{ formatDate(meta.created) }</dd>
                                <dt>updated</dt>
                                <dd>{ formatDate(meta.updated) }</dd>
                                <dt>by</dt>
                                <dd>{ meta.user || "—" }</dd>
                                <dt>comment</dt>
                                <dd>{ meta.comment || "—" }</dd>
                            </dl>
                            <UI.Button onClick={ handleDownload } type="button">
                                download { meta.file }
                            </UI.Button>
                        </>
                    ) }
                </section>
            ) }

            { usage && (
                <UI.Form onSubmit={ handleSubmit }>
                    <UI.Field>
                        <UI.FieldLabel required>
                            { meta ? `Replacement ${ noun }` : noun.charAt(0).toUpperCase() + noun.slice(1) } ({ accept.join(", ") })
                        </UI.FieldLabel>
                        <input accept={ accept.join(",") } disabled={ busy } ref={ fileInput } type="file" />
                        <UI.FieldDescription>
                            { isImage
                                ? "Checked on upload by its contents: PNG, JPEG or SVG."
                                : "Validated on upload; errors and warnings from the service are listed below." }
                        </UI.FieldDescription>
                    </UI.Field>
                    <UI.Field>
                        <UI.FieldLabel required>Comment</UI.FieldLabel>
                        <UI.TextInput disabled={ busy } onChange={ setComment } placeholder="what changed, and why" value={ comment } />
                        <UI.FieldDescription>
                            Recorded with your name and the time in the change history of this application.
                        </UI.FieldDescription>
                    </UI.Field>
                    { meta && (
                        <UI.Field>
                            <label>
                                <input
                                    checked={ overwrite }
                                    disabled={ busy }
                                    onChange={ (event) => setOverwrite(event.target.checked) }
                                    type="checkbox"
                                />
                                { " " }
                                Replace current { noun }
                            </label>
                        </UI.Field>
                    ) }
                    <div>
                        <UI.Button $appearance="primary" disabled={ busy } type="submit">
                            { busy ? "uploading…" : "upload" }
                        </UI.Button>
                        { progress !== null && <span className="progress"> { progress }%</span> }
                    </div>
                </UI.Form>
            ) }

            { result && (
                <section className={ `outcome ${ result.ok ? "stored" : "rejected" }` }>
                    <h3>
                        { result.ok
                            ? `Stored as ${ result.stored }`
                            : "Upload rejected — nothing was stored" }
                    </h3>
                    { !result.ok && result.errors.length > 0 && (
                        <>
                            <p>Fix these in the { noun } and upload again:</p>
                            <ul className="errors">
                                { result.errors.map((error) => <li key={ error }>{ error }</li>) }
                            </ul>
                        </>
                    ) }
                    { result.warnings.length > 0 && (
                        <>
                            <p>
                                { result.warnings.length } { result.warnings.length === 1 ? "note" : "notes" }
                                { result.ok ? ` — the ${ noun } is in use as it is. ` : ". " }
                                { !isImage && "A token the template does not carry is simply never filled; that is fine when the omission is intended." }
                            </p>
                            <ul className="warnings">
                                { result.warnings.map((warning) => <li key={ warning }>{ warning }</li>) }
                            </ul>
                        </>
                    ) }
                </section>
            ) }
        </Styled>
    );
}
